import React, { useState } from 'react';
import styled from 'styled-components';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { FiPaperclip, FiUpload, FiEye, FiTrash2, FiFile, FiImage, FiAlertCircle } from 'react-icons/fi';
import { storage, db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { colors } from '../theme/colors';

/**
 * Reusable one-attachment-per-transaction panel.
 *
 * Gated by the `transaction_attachments` module flag — the panel renders nothing
 * when the flag is off, so dropping it into a form is safe for every company.
 *
 * Persists a single file per transaction. Metadata lives on the transaction doc
 * under `attachment: { id, fileName, size, mimeType, url, storagePath,
 * uploadedBy, uploadedAt }`. Binary lives at
 * `companies/{companyId}/attachments/{transactionId}/{filename}`.
 *
 * Props:
 *   transactionId  Firestore doc id under companies/{id}/transactions.
 *   attachment     Current attachment object on the txn doc (or undefined).
 *   onChange       Callback ({attachment}) fired after upload/delete so the
 *                  parent form can refresh its local state.
 *   canEdit        Boolean — controls whether upload/delete buttons render.
 *   compact        Optional — render a compact inline strip instead of card.
 */
const ACCEPTED = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const MAX_MB = 10;

const AttachmentPanel = ({ transactionId, attachment, onChange, canEdit = true, compact = false }) => {
  const { activeCompany, currentUser, isModuleEnabled } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const enabled = isModuleEnabled && isModuleEnabled('transaction_attachments');
  if (!enabled) return null;

  // Without a saved transactionId we have nowhere to anchor the file. Show
  // a hint so the user knows to save the draft first.
  if (!transactionId) {
    return (
      <Card>
        <Header><FiPaperclip /> Attachment</Header>
        <Hint><FiAlertCircle size={14} /> Save the document first, then attach a file.</Hint>
      </Card>
    );
  }

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    upload(file);
  };

  const upload = async (file) => {
    setError('');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      setError('Only PDF, JPG or PNG files are allowed.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setBusy(true);
    try {
      // Use timestamp prefix so re-uploading same filename doesn't overwrite.
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `companies/${activeCompany.id}/attachments/${transactionId}/${Date.now()}_${safeName}`;
      const r = ref(storage, storagePath);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);

      // If there's a previous attachment, delete its binary now that we have a new one.
      if (attachment?.storagePath) {
        try { await deleteObject(ref(storage, attachment.storagePath)); } catch { /* best-effort */ }
      }

      const meta = {
        id: `att_${Date.now()}`,
        fileName: file.name,
        size: file.size,
        mimeType: file.type,
        url,
        storagePath,
        uploadedBy: currentUser?.uid || null,
        uploadedAt: new Date().toISOString()
      };

      // Persist on the transaction doc
      await updateDoc(doc(db, 'companies', activeCompany.id, 'transactions', transactionId), {
        attachment: meta
      });

      onChange && onChange({ attachment: meta });
    } catch (err) {
      console.error('Attachment upload failed:', err);
      setError('Upload failed: ' + (err.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!attachment) return;
    if (!window.confirm(`Remove attachment "${attachment.fileName}"?`)) return;
    setBusy(true);
    setError('');
    try {
      if (attachment.storagePath) {
        try { await deleteObject(ref(storage, attachment.storagePath)); } catch { /* may already be gone */ }
      }
      await updateDoc(doc(db, 'companies', activeCompany.id, 'transactions', transactionId), {
        attachment: null
      });
      onChange && onChange({ attachment: null });
    } catch (err) {
      console.error('Attachment delete failed:', err);
      setError('Delete failed: ' + (err.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const sizeKb = (b) => (b ? (b / 1024).toFixed(b > 1024 * 1024 ? 0 : 1) + (b > 1024 * 1024 ? ' MB' : ' KB') : '');
  const FileIcon = attachment && /image\//.test(attachment.mimeType) ? FiImage : FiFile;

  return (
    <Card $compact={compact}>
      <Header><FiPaperclip /> Attachment</Header>
      {attachment ? (
        <Row>
          <FileBox>
            <FileIcon size={20} />
            <FileInfo>
              <FileName>{attachment.fileName}</FileName>
              <FileMeta>{sizeKb(attachment.size)} · uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}</FileMeta>
            </FileInfo>
          </FileBox>
          <Actions>
            <ActionBtn as="a" href={attachment.url} target="_blank" rel="noreferrer" title="Open / Download">
              <FiEye /> View
            </ActionBtn>
            {canEdit && (
              <ActionBtn $danger onClick={handleDelete} disabled={busy} title="Remove">
                <FiTrash2 /> Remove
              </ActionBtn>
            )}
          </Actions>
        </Row>
      ) : (
        canEdit ? (
          <UploadRow>
            <label>
              <input type="file" accept={ACCEPTED} onChange={handlePick} disabled={busy} style={{ display: 'none' }} />
              <UploadBtn as="span">
                <FiUpload /> {busy ? 'Uploading…' : 'Attach File'}
              </UploadBtn>
            </label>
            <Hint>PDF, JPG or PNG — max {MAX_MB} MB</Hint>
          </UploadRow>
        ) : (
          <Hint>No attachment.</Hint>
        )
      )}
      {error && <ErrorMsg><FiAlertCircle size={14} /> {error}</ErrorMsg>}
    </Card>
  );
};

// ── styled ──
const Card = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: ${p => p.$compact ? '10px 12px' : '14px 16px'};
  margin: ${p => p.$compact ? '6px 0' : '12px 0'};
`;
const Header = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 10px;
`;
const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
`;
const FileBox = styled.div`
  display: flex; align-items: center; gap: 10px; flex: 1; min-width: 200px;
  color: ${colors.primary.main};
`;
const FileInfo = styled.div`display: flex; flex-direction: column;`;
const FileName = styled.div`font-size: 13px; font-weight: 600; color: ${colors.text.primary}; word-break: break-all;`;
const FileMeta = styled.div`font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px;`;
const Actions = styled.div`display: flex; gap: 8px;`;
const ActionBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 12px;
  font-size: 12px; font-weight: 600;
  background: ${p => p.$danger ? '#FEE2E2' : colors.primary.main + '15'};
  color: ${p => p.$danger ? colors.error.main : colors.primary.main};
  border: 1px solid ${p => p.$danger ? '#FCA5A5' : colors.primary.main + '40'};
  border-radius: 6px;
  cursor: pointer; text-decoration: none;
  &:hover { background: ${p => p.$danger ? '#FCA5A5' : colors.primary.main + '25'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const UploadRow = styled.div`display: flex; align-items: center; gap: 12px; flex-wrap: wrap;`;
const UploadBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  font-size: 13px; font-weight: 600;
  background: ${colors.primary.main};
  color: white;
  border: none; border-radius: 6px;
  cursor: pointer;
  &:hover { background: ${colors.primary.dark}; }
`;
const Hint = styled.div`font-size: 11px; color: ${colors.text.tertiary};`;
const ErrorMsg = styled.div`
  margin-top: 8px;
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: ${colors.error.main};
`;

export default AttachmentPanel;
